import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Alert, CircularProgress, Button, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress
} from '@mui/material';
import {
  Assessment, Refresh, BarChart, CalendarToday, PlayArrow, Science
} from '@mui/icons-material';
import axios from 'axios';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';

interface RatingComponent {
  no: number;
  component: string;
  display_name: string;
  r_square: number;
  p_value: number;
  weights: number;
  significant: boolean;
}

interface RatingComponentsSummary {
  components: RatingComponent[];
  total_weight: number;
  analysis_timestamp: string;
  data_source?: string;
  cached_at?: string;
}

interface RatingStatus {
  year: number;
  status: 'no_master_data' | 'needs_regression' | 'needs_calculation' | 'processing' | 'ready';
  message: string;
  has_analysis: boolean;
  has_master_data: boolean;
  master_data_count: number;
  last_calculated?: string;
}

const RatingComponents: React.FC = () => {
  const theme = useTheme();
  const [results, setResults] = useState<RatingComponentsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [yearFilter, setYearFilter] = useState<number | ''>('');
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [status, setStatus] = useState<RatingStatus | undefined>(undefined);
  const [calcDialogOpen, setCalcDialogOpen] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calcProgress, setCalcProgress] = useState(0);
  const [calcMessage, setCalcMessage] = useState('');
  const [calcYear, setCalcYear] = useState<number | null>(null);

  const fetchAvailableYears = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/v1/master-data', {
        headers: { Authorization: `Bearer ${token}` },
        params: { per_page: 1000 }
      });
      if (response.data?.data) {
        const yearsSet = new Set<number>();
        response.data.data.forEach((item: any) => { yearsSet.add(item.year); });
        const years = Array.from(yearsSet).sort((a, b) => b - a);
        setAvailableYears(years);
        // Only set yearFilter if we have years and user hasn't selected one
        if (years.length > 0 && !yearFilter) {
          setYearFilter(years[0]);
        }
      }
    } catch (error) {
      // On error, leave availableYears empty to show empty state
      setAvailableYears([]);
    }
  };

  const fetchStatus = async () => {
    if (!yearFilter) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `/api/v1/analytics/rating-components/status?year=${yearFilter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatus(res.data);
      await fetchResults();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch status');
      setLoading(false);
    }
  };

  const fetchResults = async () => {
    if (!yearFilter) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `/api/v1/analytics/rating-components?year=${yearFilter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResults(res.data);
      setLoading(false);
    } catch (err: any) {
      setResults(null);
      setLoading(false);
    }
  };

  const handleRunCalc = async () => {
    if (!yearFilter) return;
    setCalcYear(yearFilter);
    setCalcDialogOpen(true);
    setIsCalculating(true);
    setCalcProgress(10);
    setCalcMessage('Starting calculation...');

    try {
      const token = localStorage.getItem('token');
      setCalcProgress(30);
      setCalcMessage('Calculating rating components...');
      await axios.post(
        '/api/v1/analytics/rating-components/run',
        { year: yearFilter, force: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCalcProgress(90);
      setCalcMessage('Finalizing...');
      await fetchStatus();
      setCalcProgress(100);
      setCalcMessage('Complete!');
      setTimeout(() => {
        setIsCalculating(false);
        setCalcDialogOpen(false);
      }, 500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to run calculation');
      setIsCalculating(false);
      setCalcDialogOpen(false);
    }
  };

  useEffect(() => { fetchAvailableYears(); }, []);
  useEffect(() => { if (yearFilter) fetchStatus(); }, [yearFilter]);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
  );

  if (!yearFilter) return (
    <Box sx={{ textAlign: 'center', mt: 6 }}>
      <Assessment sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
      <Typography variant="h6" color="text.secondary">Select a year to view rating components</Typography>
    </Box>
  );

  const PageHeader = () => (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>Rating Components</Typography>
          <Typography variant="body1" color="text.secondary">Component weights and regression analysis results</Typography>
        </Box>
        <Button variant="contained" startIcon={<Refresh />} onClick={handleRunCalc} disabled={isCalculating}>
          {isCalculating ? 'Running...' : 'Re-run'}
        </Button>
      </Box>
    </Box>
  );

  const YearFilterCard = () => (
    <Card sx={{ border: '2px solid', borderColor: 'primary.main', mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <CalendarToday sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Filter by Year</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Year</InputLabel>
                <Select value={yearFilter} label="Year" onChange={(e) => setYearFilter(e.target.value as number)}>
                  {availableYears.map((year) => <MenuItem key={year} value={year}>{year}</MenuItem>)}
                </Select>
              </FormControl>
              {status?.has_master_data && <Chip label={`${status.master_data_count} records`} color="info" />}
              <Button variant="contained" size="small" startIcon={<PlayArrow />} onClick={handleRunCalc} disabled={isCalculating}>
                {isCalculating ? 'Running...' : (status?.has_analysis ? 'Re-run' : 'Run Calculation')}
              </Button>
              {status?.has_analysis && <Chip label="Analysis Ready" color="success" />}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const CalculationDialog = () => (
    <Dialog open={calcDialogOpen} maxWidth="sm" fullWidth>
      <DialogTitle>Running Rating Components Calculation</DialogTitle>
      <DialogContent>
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Processing year {calcYear}...</Typography>
          <LinearProgress variant="determinate" value={calcProgress} sx={{ height: 10, borderRadius: 5, mb: 2 }} />
          <Typography variant="body2" sx={{ mb: 2 }}>{calcProgress}% complete</Typography>
          <Alert severity={isCalculating ? "info" : "success"}>{calcMessage || (isCalculating ? 'Calculating...' : 'Complete!')}</Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCalcDialogOpen(false)} disabled={isCalculating}>{isCalculating ? 'Please wait...' : 'Close'}</Button>
      </DialogActions>
    </Dialog>
  );

  if (status?.status === 'no_master_data') return (
    <Box>
      <PageHeader />
      <YearFilterCard />
      <Alert severity="warning"><Typography variant="h6">No Master Data for Year {yearFilter}</Typography><Typography variant="body2">Please import master data first.</Typography></Alert>
      <CalculationDialog />
    </Box>
  );

  if (status?.status === 'needs_regression') return (
    <Box>
      <PageHeader />
      <YearFilterCard />
      <Alert severity="warning">
        <Typography variant="h6">Run Regression Analysis First</Typography>
        <Typography variant="body2">Please run regression analysis before calculating rating components.</Typography>
      </Alert>
      <CalculationDialog />
    </Box>
  );

  if (status?.status === 'needs_calculation') return (
    <Box>
      <PageHeader />
      <YearFilterCard />
      <Card sx={{ mt: 4, textAlign: 'center' }}>
        <CardContent sx={{ p: 4 }}>
          <Science sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>Rating Components Not Calculated</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>Click "Run Calculation" button above to calculate for year {yearFilter}.</Typography>
        </CardContent>
      </Card>
      <CalculationDialog />
    </Box>
  );

  return (
    <Box>
      <PageHeader />
      <YearFilterCard />
      <CalculationDialog />

      {results && results.components && results.components.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              <BarChart sx={{ mr: 1, verticalAlign: 'middle' }} />
              Component Performance Summary
            </Typography>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>NO</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>COMPONENTS</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">R SQUARE</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">P VALUE</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">WEIGHTS</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>SIGNIFICANCE</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.components.map((component) => (
                    <TableRow key={component.component} hover>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{component.no}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{component.display_name}</TableCell>
                      <TableCell align="right">
                        <Chip label={component.r_square.toFixed(4)} color={component.r_square >= 0.8 ? 'success' : component.r_square >= 0.6 ? 'warning' : 'error'} size="small" sx={{ fontWeight: 600, fontFamily: 'monospace' }} />
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={component.p_value < 0.001 ? component.p_value.toExponential(2) : component.p_value.toFixed(4)} color={component.p_value < 0.05 ? 'success' : component.p_value < 0.1 ? 'warning' : 'error'} size="small" sx={{ fontFamily: 'monospace' }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{(component.weights * 100).toFixed(3)}%</TableCell>
                      <TableCell>
                        {component.significant ? <Chip label="Significant" color="success" size="small" /> : <Chip label="Not Significant" color="error" size="small" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Summary Statistics</Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box><Typography variant="body2" color="text.secondary">Total Components</Typography><Typography variant="h6" sx={{ fontWeight: 600 }}>{results.components.length}</Typography></Box>
                <Box><Typography variant="body2" color="text.secondary">Significant</Typography><Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>{results.components.filter(c => c.significant).length}</Typography></Box>
                <Box><Typography variant="body2" color="text.secondary">Total Weight</Typography><Typography variant="h6" sx={{ fontWeight: 600 }}>{(results.total_weight * 100).toFixed(3)}%</Typography></Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {(!results || !results.components || results.components.length === 0) && !loading && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Assessment sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>No Rating Components Data Available</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Please run calculation first</Typography>
            <Button variant="contained" startIcon={<PlayArrow />} onClick={handleRunCalc}>Run Calculation</Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default RatingComponents;
